import { Component } from '@theme/component';

const storageKey = 'solune:wishlist:v1';
const subscribers = new Set();
let storageUnavailable = false;

function imageUrl(value) {
  if (typeof value !== 'string' || !value) return '';
  try {
    const url = new URL(value, window.location.origin);
    if (!['https:', 'http:'].includes(url.protocol)) return '';
    url.searchParams.set('width', '320');
    return url.href;
  } catch {
    return '';
  }
}

function validItem(item) {
  return item && /^\d+$/.test(String(item.id)) && typeof item.handle === 'string' &&
    /^[\p{L}\p{N}][\p{L}\p{N}_-]*$/u.test(item.handle) && typeof item.title === 'string';
}

function readItems() {
  let raw;
  try {
    raw = localStorage.getItem(storageKey);
  } catch {
    storageUnavailable = true;
    return null;
  }
  try {
    const value = JSON.parse(raw || '[]');
    if (!Array.isArray(value)) return [];
    return [...new Map(value.filter(validItem).map((item) => [String(item.id), {
      id: String(item.id), handle: item.handle, title: item.title.slice(0, 300),
      image: imageUrl(item.image),
    }])).values()];
  } catch {
    return [];
  }
}

let items = readItems() || [];
const isSaved = (id) => items.some((item) => item.id === id);
const notify = () => subscribers.forEach((callback) => callback());

function saveItems(next, shouldNotify = true) {
  items = next;
  try {
    localStorage.setItem(storageKey, JSON.stringify(items));
    storageUnavailable = false;
  } catch {
    storageUnavailable = true;
  }
  if (shouldNotify) notify();
}

function announce(key) {
  const drawer = document.querySelector('wishlist-drawer');
  const status = drawer?.querySelector('[data-wishlist-status]');
  if (status) status.textContent = storageUnavailable ? drawer.dataset.storageError : drawer.dataset[key];
}

function removeItem(id) {
  if (!storageUnavailable) items = readItems() || items;
  saveItems(items.filter((item) => item.id !== id));
  announce('removed');
}

window.addEventListener('storage', (event) => {
  if (event.key !== storageKey && event.key !== null) return;
  items = readItems() || items;
  notify();
});

window.addEventListener('pageshow', () => {
  if (!storageUnavailable) items = readItems() || items;
  notify();
});

class WishlistButton extends Component {
  static observedAttributes = ['hidden', 'data-product-id'];

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue && (name !== 'hidden' || newValue !== null)) {
      queueMicrotask(() => { if (this.isConnected) this.update(); });
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.toggle);
    subscribers.add(this.update);
    this.update();
  }

  updatedCallback() {
    super.updatedCallback();
    this.update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.toggle);
    subscribers.delete(this.update);
  }

  update = () => {
    this.button = this.querySelector('button');
    if (!this.button || !validItem({ id: this.dataset.productId, ...this.dataset })) return;
    const saved = isSaved(this.dataset.productId);
    this.button.setAttribute('aria-pressed', String(saved));
    this.button.setAttribute('aria-label', saved ? this.dataset.removeLabel : this.dataset.saveLabel);
    const label = this.querySelector('[data-wishlist-label]');
    if (label) label.textContent = saved ? this.dataset.savedText : this.dataset.saveText;
    this.hidden = false;
  };

  toggle = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!storageUnavailable) items = readItems() || items;
    const id = this.dataset.productId;
    if (isSaved(id)) {
      removeItem(id);
    } else {
      saveItems([{ id, handle: this.dataset.handle, title: this.dataset.title, image: imageUrl(this.dataset.image) }, ...items]);
      announce('added');
    }
  };
}

class WishlistLink extends Component {
  static observedAttributes = ['hidden'];

  attributeChangedCallback(name, oldValue, newValue) {
    if (newValue !== null) queueMicrotask(() => { if (this.isConnected) this.update(); });
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.open);
    subscribers.add(this.update);
    this.update();
  }

  updatedCallback() {
    super.updatedCallback();
    this.update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.open);
    subscribers.delete(this.update);
  }

  update = () => {
    this.button = this.querySelector('button');
    const count = this.querySelector('[data-wishlist-count]');
    if (!count || !this.button) return;
    count.textContent = String(items.length);
    count.hidden = items.length === 0;
    this.hidden = false;
  };

  open = () => document.querySelector('wishlist-drawer')?.open(this.button);
}

class WishlistDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.list = this.querySelector('[data-wishlist-list]');
    this.template = this.querySelector('[data-wishlist-row]');
    this.dialog.addEventListener('click', this.onClick);
    this.dialog.addEventListener('close', this.onClose);
    subscribers.add(this.update);
    this.update();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.dialog.removeEventListener('click', this.onClick);
    this.dialog.removeEventListener('close', this.onClose);
    subscribers.delete(this.update);
  }

  open(trigger) {
    if (this.dialog.open) return;
    this.trigger = trigger;
    if (!storageUnavailable) items = readItems() || items;
    this.dialog.showModal();
    this.update();
  }

  onClose = () => {
    this.abort?.abort();
    if (this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
  };

  onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest('[data-wishlist-close]')) this.dialog.close();
    if (target === this.dialog) {
      const rect = this.dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
    }
    const remove = target.closest('[data-wishlist-remove]');
    if (remove) {
      const row = remove.closest('[data-wishlist-id]');
      const nextId = row.nextElementSibling?.dataset.wishlistId || row.previousElementSibling?.dataset.wishlistId;
      removeItem(row.dataset.wishlistId);
      const next = [...this.list.children].find((item) => item.dataset.wishlistId === nextId);
      (next?.querySelector('[data-wishlist-remove]') || this.querySelector('[data-wishlist-close]')).focus();
    }
    const retry = target.closest('[data-wishlist-retry]');
    if (retry) {
      const row = retry.closest('[data-wishlist-id]');
      const item = items.find((entry) => entry.id === row.dataset.wishlistId);
      if (item) this.loadProduct(row, item, this.abort.signal);
    }
  };

  update = () => {
    this.querySelector('[data-wishlist-count]').textContent = String(items.length);
    const warning = this.querySelector('[data-wishlist-warning]');
    warning.hidden = !storageUnavailable;
    warning.textContent = storageUnavailable ? this.dataset.storageError : '';
    if (this.dialog.open) this.renderItems();
  };

  productUrl(handle) {
    const root = window.Shopify?.routes?.root || `${this.dataset.root.replace(/\/$/, '')}/`;
    return `${root}products/${encodeURIComponent(handle)}`;
  }

  showImage(row, source) {
    const url = imageUrl(source);
    if (!url) return;
    const link = row.querySelector('[data-wishlist-image-link]');
    if (link.querySelector('img')?.src === url) return;
    const image = document.createElement('img');
    image.src = url;
    image.alt = '';
    image.width = 128;
    image.height = 160;
    image.loading = 'lazy';
    image.addEventListener('error', () => image.remove(), { once: true });
    link.replaceChildren(image);
  }

  renderItems() {
    this.abort?.abort();
    this.abort = new AbortController();
    const signal = this.abort.signal;
    this.querySelector('[data-wishlist-empty]').hidden = items.length > 0;
    this.querySelector('[data-wishlist-footer]').hidden = items.length === 0;
    const jobs = items.map((item) => {
      const row = this.template.content.firstElementChild.cloneNode(true);
      row.dataset.wishlistId = item.id;
      const url = this.productUrl(item.handle);
      const title = row.querySelector('[data-wishlist-title]');
      title.textContent = item.title;
      title.href = url;
      row.querySelector('[data-wishlist-image-link]').href = url;
      row.querySelector('[data-wishlist-view]').href = url;
      row.querySelector('[data-wishlist-remove]').setAttribute('aria-label', this.dataset.removeLabel.replace('[product]', item.title));
      const button = [...document.querySelectorAll('wishlist-button')].find((element) => element.dataset.productId === item.id);
      this.showImage(row, item.image || button?.dataset.image);
      return { row, item };
    });
    this.list.replaceChildren(...jobs.map(({ row }) => row));
    const queue = [...jobs];
    const worker = async () => {
      while (queue.length && !signal.aborted) {
        const { row, item } = queue.shift();
        await this.loadProduct(row, item, signal);
      }
    };
    for (let i = 0; i < Math.min(4, jobs.length); i++) worker();
  }

  async loadProduct(row, item, signal) {
    const price = row.querySelector('[data-wishlist-price]');
    const availability = row.querySelector('[data-wishlist-availability]');
    const retry = row.querySelector('[data-wishlist-retry]');
    const title = row.querySelector('[data-wishlist-title]');
    retry.disabled = true;
    retry.hidden = true;
    price.textContent = this.dataset.loading;
    availability.hidden = true;
    row.removeAttribute('data-load-error');
    row.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${this.productUrl(item.handle)}?section_id=wishlist-product`, {
        signal: AbortSignal.any([signal, AbortSignal.timeout(12000)]),
        headers: { Accept: 'text/html' },
      });
      if (response.status === 404) throw new Error('unavailable');
      if (!response.ok) throw new Error('load');
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      const product = document.querySelector('[data-wishlist-product]')?.dataset;
      if (!product) throw new Error('load');
      if (product.id !== item.id) throw new Error('unavailable');
      if (!product.price || !Number.isFinite(Number(product.price)) || !product.title || !product.priceText) throw new Error('load');
      if (signal.aborted || !row.isConnected) return;
      title.textContent = product.title;
      row.querySelector('[data-wishlist-remove]').setAttribute('aria-label', this.dataset.removeLabel.replace('[product]', product.title));
      price.textContent = product.priceText;
      availability.textContent = product.available === 'true' ? '' : this.dataset.soldOut;
      availability.hidden = product.available === 'true';
      retry.hidden = true;
      this.showImage(row, product.image);
      const latest = storageUnavailable ? items : readItems() || items;
      const saved = latest.find((entry) => entry.id === item.id);
      const image = imageUrl(product.image);
      if (saved && (saved.title !== product.title || saved.image !== image)) {
        saveItems(latest.map((entry) => entry.id === item.id ? { ...entry, title: product.title, image } : entry), false);
      }
    } catch (error) {
      if (signal.aborted || !row.isConnected) return;
      const unavailable = error.message === 'unavailable';
      row.setAttribute('data-load-error', '');
      price.textContent = unavailable ? this.dataset.unavailable : this.dataset.loadError;
      retry.hidden = unavailable;
      if (unavailable) {
        title.removeAttribute('href');
        row.querySelector('[data-wishlist-image-link]').removeAttribute('href');
        row.querySelector('[data-wishlist-view]').hidden = true;
      }
    } finally {
      row.removeAttribute('aria-busy');
      retry.disabled = false;
    }
  }
}

if (!customElements.get('wishlist-button')) customElements.define('wishlist-button', WishlistButton);
if (!customElements.get('wishlist-link')) customElements.define('wishlist-link', WishlistLink);
if (!customElements.get('wishlist-drawer')) customElements.define('wishlist-drawer', WishlistDrawer);
